import { LoginResponse } from "../Types/login-response";
import { ReactSession } from "react-client-session";
import ReactSessionKeys from "../../Utils/Constants/react-session-keys";
import { DbApplicationInfos } from "../Types/db-application-infos";
import { DbMetadataFieldInfos, MetadataFieldInfosCache } from "../Types/db-metadata-field-infos";
import { DbUserInfos } from "../Types/db-user-infos";
import { DbUserGroupInfos } from "../Types/db-user-group-infos";
import { SpockAgentType } from "../Types/spock-agent-type";
import moment from "moment";
import { Licences } from "../Types/licences";
import { AclTypes } from "../Types/acl-types";
import { DbUserGroupRoleInfos } from "../Types/db-user-group-role-infos";
import { LoginPlatformSettings } from "../Types/login-platform-settings";
import { PlatformMode } from "../Types/platform-mode";
import { SharedAccount } from "../Types/get-shared-accounts-response";
import { PlanningView } from "../Types/planning-view";
import { PlanningMode } from "../Types/planning-mode";
import { GridColumnVisibilityModel, GridDensity, GridPinnedColumnFields, GridSortModel } from "@mui/x-data-grid";
import { DbMetadataFieldValueInfos, MetadataFieldValueInfosCache } from "../Types/db-metadata-field-value-infos";
import { IDbBaseMetadataValueInfos, BaseMetadataValueInfosCache } from "../Types/i-db-base-metadata-value-infos";
import { GridPinnedColumns } from "@mui/x-data-grid-pro";

const convertValueObjectToCache = (valueObject: IDbBaseMetadataValueInfos | null | undefined) => {
    if (!valueObject) return null;
    return {
        fn: valueObject.fieldName,
        ft: valueObject.fieldType,
        id: valueObject.id,
        v: valueObject.value,
        vl: valueObject.valueLabel,
    } as BaseMetadataValueInfosCache;
};
const convertMetaValuesToCache = (values: DbMetadataFieldValueInfos[] | null | undefined) => {
    if (!values || values.length === 0) return null;
    return values.map(
        (v) =>
            new MetadataFieldValueInfosCache({
                id: v.id,
                eid: v.externalId,
                lb: v.label,
                vo: convertValueObjectToCache(v.valueObject),
                v: v.value,
            })
    );
};
const mapFieldInfosToCache = (field: DbMetadataFieldInfos) => {
    return new MetadataFieldInfosCache({
        id: field.id,
        srch: field.isSearchable,
        mdtr: field.isMandatory,
        pop: field.allowPopulate,
        bndlmkr: field.isUsedOnBundleMaker,
        lr: field.layer,
        bt: field.baseType,
        ot: field.objectType,
        lb: field.label,
        tn: field.tagName,
        v: convertMetaValuesToCache(field.values),
        oc: field.objectClasses,
    });
};

const convertFromCacheToValueObject = (valueObject: BaseMetadataValueInfosCache | null | undefined) => {
    if (!valueObject) return null;
    return {
        fieldName: valueObject.fn,
        fieldType: valueObject.ft,
        id: valueObject.id,
        value: valueObject.v,
        valueLabel: valueObject.vl,
    } as IDbBaseMetadataValueInfos;
};
const convertFromCacheToMetaValue = (values: MetadataFieldValueInfosCache[] | null | undefined) => {
    if (!values || values.length === 0) return null;
    return values.map(
        (v) =>
            new DbMetadataFieldValueInfos({
                id: v.id,
                externalId: v.eid,
                label: v.lb,
                valueObject: convertFromCacheToValueObject(v.vo),
                value: v.v,
            })
    );
};

const mapCacheFieldsToBase = (field: MetadataFieldInfosCache) => {
    return new DbMetadataFieldInfos({
        id: field.id,
        isSearchable: field.srch,
        isMandatory: field.mdtr,
        allowPopulate: field.pop,
        isUsedOnBundleMaker: field.bndlmkr,
        layer: field.lr,
        baseType: field.bt,
        objectType: field.ot,
        label: field.lb,
        tagName: field.tn,
        values: convertFromCacheToMetaValue(field.v),
        objectClasses: field.oc,
    });
};

export class CacheService {
    currentAuth: LoginResponse | null = null;
    metadataFields: MetadataFieldInfosCache[] | null = null;
    appData: DbApplicationInfos | null = null;
    platformSettings: LoginPlatformSettings | null = null;
    isEmulated: boolean = false;
    groupRoles: DbUserGroupRoleInfos[] | null = null;
    snackbarEvents: boolean = true;
    catchPhrase: string = "Browse. Buy. Broadcast";
    oktaCodeVerifier: string | null = null;
    oktaPid: number | null = null;
    sharedAccounts: SharedAccount[] = [];
    activeFeatures: string[] | null = null;
    clipListLayout: "grid" | "list" = "list";
    browserLayout: "grid" | "list" = "grid";
    dragOrderLists: any;
    downloadedFilesPath: any;
    driveHeadId: number | undefined;
    pageFilters: any;
    pageCache: any;
    drawerOpen: boolean = true;
    specialPagesCache: any;
    densityPerPage: any;
    leftPanelWidth: number | null = null;
    rightPanelWidth: number | null = null;
    lastUpdatedMediaTimestamp: number = new Date().getTime();
    rowsPerPage: any;
    tablesLayout: {
        [key: string]: {
            columnsOrder: { [key: string]: number };
            columnsVisibility: GridColumnVisibilityModel;
            columnsSort: GridSortModel;
            columnsPin: GridPinnedColumnFields;
            columnFilters: boolean;
        };
    } = {};
    constructor() {
        this.Resync();
    }

    Resync(): void {
        this.currentAuth = ReactSession.get(ReactSessionKeys.CURRENT_AUTH) ?? null;
        this.metadataFields = ReactSession.get(ReactSessionKeys.METADATA_FIELDS) ?? null;
        this.appData = ReactSession.get(ReactSessionKeys.APP_DATA) ?? null;
        this.platformSettings = ReactSession.get(ReactSessionKeys.PLATFORM_SETTINGS) ?? null;
        this.isEmulated = ReactSession.get(ReactSessionKeys.EMULATE_ENABLED) ?? false;
        this.snackbarEvents = ReactSession.get(ReactSessionKeys.SNACKBAR_EVENTS_ENABLED) ?? true;
        this.catchPhrase = ReactSession.get(ReactSessionKeys.CATCH_PHRASE) ?? "Browse. Buy. Broadcast";
        this.oktaCodeVerifier = ReactSession.get(ReactSessionKeys.OKTA_CODE_VERIFIER) ?? null;
        this.oktaPid = ReactSession.get(ReactSessionKeys.OKTA_PLATFORM_ID) ?? null;
        this.sharedAccounts = ReactSession.get(ReactSessionKeys.SHARED_ACCOUNTS) ?? [];
        this.activeFeatures = ReactSession.get(ReactSessionKeys.ACTIVE_FEATURES) ?? null;
        this.clipListLayout = ReactSession.get(ReactSessionKeys.CLIP_LIST_LAYOUT) ?? "list";
        this.browserLayout = ReactSession.get(ReactSessionKeys.BROWSER_LAYOUT) ?? "grid";
        this.dragOrderLists = ReactSession.get(ReactSessionKeys.DRAG_ORDER_LISTS) ?? {};
        this.downloadedFilesPath = ReactSession.get(ReactSessionKeys.DOWNLOADED_FILES_PATH) ?? {};
        this.driveHeadId = ReactSession.get(ReactSessionKeys.DRIVE_HEAD_ID) || undefined;
        this.pageFilters = ReactSession.get(ReactSessionKeys.PAGE_FILTERS) ?? {};
        this.pageCache = ReactSession.get(ReactSessionKeys.PAGE_CACHE) ?? {};
        this.drawerOpen = ReactSession.get(ReactSessionKeys.DRAWER_OPEN) ?? true;
        // this.density = ReactSession.get(ReactSessionKeys.DENSITY) ?? "standard";
        this.densityPerPage = ReactSession.get(ReactSessionKeys.DENSITY_PER_PAGE) ?? {};
        this.leftPanelWidth = ReactSession.get(ReactSessionKeys.LEFT_PANEL_WIDTH) ?? 375;
        this.rightPanelWidth = ReactSession.get(ReactSessionKeys.RIGHT_PANEL_WIDTH) ?? 375;
        this.lastUpdatedMediaTimestamp =
            ReactSession.get(ReactSessionKeys.LAST_UPDATED_MEDIA_TIMESTAMP) ?? new Date().getTime();
        this.rowsPerPage = ReactSession.get(ReactSessionKeys.ROWS_PER_PAGE) ?? {};
        this.tablesLayout = ReactSession.get(ReactSessionKeys.TABLES_LAYOUT) ?? {};
    }

    //#region manipulate cache and session
    SetAuth(authObject: LoginResponse): void {
        ReactSession.set(ReactSessionKeys.CURRENT_AUTH, authObject);
        this.currentAuth = authObject;
    }

    SetUser(user: DbUserInfos): void {
        if (this.currentAuth === null) return;
        this.currentAuth.user = user;
        ReactSession.set(ReactSessionKeys.CURRENT_AUTH, this.currentAuth);
    }

    UnsetAuth(): void {
        ReactSession.remove(ReactSessionKeys.AUTHKEY);
        ReactSession.remove(ReactSessionKeys.SPOCK_AGENT_TYPE);
        ReactSession.remove(ReactSessionKeys.CUSTOMER_EXT_ID_NAME);
        ReactSession.remove(ReactSessionKeys.EMULATE_STORE_ORIGIN);
        ReactSession.remove(ReactSessionKeys.EMULATE_ENABLED);
        ReactSession.remove(ReactSessionKeys.CURRENT_AUTH);
        ReactSession.remove(ReactSessionKeys.OKTA_CODE_VERIFIER);
        ReactSession.remove(ReactSessionKeys.OKTA_PLATFORM_ID);
        ReactSession.remove(ReactSessionKeys.SHARED_ACCOUNTS);

        this.currentAuth = null;
        ReactSession.set(ReactSessionKeys.ISAUTH, false);
    }

    SetEmulateUser(): void {
        ReactSession.set(ReactSessionKeys.EMULATE_STORE_ORIGIN, this.currentAuth);
        ReactSession.set(ReactSessionKeys.EMULATE_ENABLED, true);
        this.isEmulated = true;
    }

    SetCatchPhrase(catchPhrase: string): void {
        ReactSession.set(ReactSessionKeys.CATCH_PHRASE, catchPhrase);
        this.catchPhrase = catchPhrase;
    }

    UnsetEmulateUser(): void {
        const originAuthData = ReactSession.get(ReactSessionKeys.EMULATE_STORE_ORIGIN);

        ReactSession.set(ReactSessionKeys.CURRENT_AUTH, originAuthData);
        this.currentAuth = originAuthData;

        ReactSession.set(ReactSessionKeys.EMULATE_ENABLED, false);
        this.isEmulated = false;
        ReactSession.remove(ReactSessionKeys.EMULATE_STORE_ORIGIN);
    }

    SetMetadataFields(metadataFields: DbMetadataFieldInfos[] | null): void {
        const filteredMetadata = metadataFields?.filter((x) => x.tagName !== "vodRatingsDescriptor") ?? null;
        const cachedMetadata = filteredMetadata?.map((x) => mapFieldInfosToCache(x)) ?? null;
        ReactSession.set(ReactSessionKeys.METADATA_FIELDS, cachedMetadata);
        this.metadataFields = cachedMetadata;
    }

    SetAppData(appData: DbApplicationInfos | null): void {
        ReactSession.set(ReactSessionKeys.APP_DATA, appData);
        this.appData = appData;
    }

    SetPlatformSettings(settings: LoginPlatformSettings | null): void {
        ReactSession.set(ReactSessionKeys.PLATFORM_SETTINGS, settings);
        this.platformSettings = settings;

        if (settings?.catchPhrase) this.SetCatchPhrase(settings.catchPhrase);
    }

    SetOktaCodeVerifier(codeVerifier: string | null, platformId: number): void {
        ReactSession.set(ReactSessionKeys.OKTA_CODE_VERIFIER, codeVerifier);
        ReactSession.set(ReactSessionKeys.OKTA_PLATFORM_ID, platformId);
        this.oktaCodeVerifier = codeVerifier;
        this.oktaPid = platformId;
    }

    SetSharedAccounts(accounts): void {
        ReactSession.set(ReactSessionKeys.SHARED_ACCOUNTS, accounts);
        this.sharedAccounts = accounts;
    }

    SetActiveFeatures(activeFeatures: string[] | null): void {
        ReactSession.set(ReactSessionKeys.ACTIVE_FEATURES, activeFeatures);
        this.activeFeatures = activeFeatures;
    }

    SetClipListLayout(layout: "grid" | "list"): void {
        ReactSession.set(ReactSessionKeys.CLIP_LIST_LAYOUT, layout);
    }

    SetBrowserLayout(layout: "grid" | "list"): void {
        ReactSession.set(ReactSessionKeys.BROWSER_LAYOUT, layout);
    }

    SetDragListOrder(cacheListKey: string, value): void {
        const current = this.dragOrderLists;
        current[cacheListKey] = value;
        ReactSession.set(ReactSessionKeys.DRAG_ORDER_LISTS, current);
    }

    SetDowloadedFilesPath(fileId: number, path: string) {
        const current = this.downloadedFilesPath;
        current[fileId.toString()] = path;
        ReactSession.set(ReactSessionKeys.DOWNLOADED_FILES_PATH, current);
    }

    SetPlatformDriveHeadId(id: number) {
        ReactSession.set(ReactSessionKeys.DRIVE_HEAD_ID, id);
        this.driveHeadId = id;
    }
    SetPageFilterByKey(page: string, key: string, value: any) {
        const current = this.pageFilters;
        current[page] = current[page] ? { ...current[page], [key]: value } : { [key]: value };
        ReactSession.set(ReactSessionKeys.PAGE_FILTERS, current);
    }
    SetPageCache(page: string, value: any) {
        const current = this.pageCache;
        current[page] = value;
        ReactSession.set(ReactSessionKeys.PAGE_CACHE, current);
    }

    SetDrawerOpen(open: boolean) {
        this.drawerOpen = open;
        ReactSession.set(ReactSessionKeys.DRAWER_OPEN, open);
    }

    SetDensity(density: GridDensity, page: string) {
        const current = this.densityPerPage;
        current[page] = density;
        ReactSession.set(ReactSessionKeys.DENSITY_PER_PAGE, current);
    }

    SetLeftPanelWidth(width: number) {
        this.leftPanelWidth = width;
        ReactSession.set(ReactSessionKeys.LEFT_PANEL_WIDTH, width);
    }

    SetRightPanelWidth(width: number) {
        this.rightPanelWidth = width;
        ReactSession.set(ReactSessionKeys.RIGHT_PANEL_WIDTH, width);
    }

    SetLastUpdatedMediaTimestamp(timestamp: number) {
        this.lastUpdatedMediaTimestamp = timestamp;
        ReactSession.set(ReactSessionKeys.LAST_UPDATED_MEDIA_TIMESTAMP, timestamp);
    }

    SetRowsPerPage(rowsPerPage: number, page: string) {
        const current = this.rowsPerPage;
        current[page] = rowsPerPage;
        ReactSession.set(ReactSessionKeys.ROWS_PER_PAGE, current);
    }

    SetTableSort(tableKey: string, sortModel: GridSortModel) {
        const current = this.tablesLayout;
        current[tableKey] = current[tableKey] ?? {};
        current[tableKey].columnsSort = sortModel;
        ReactSession.set(ReactSessionKeys.TABLES_LAYOUT, current);
    }
    SetTableColumnOrder(tableKey: string, columnsOrder: { [key: string]: number }) {
        const current = this.tablesLayout;
        current[tableKey] = current[tableKey] ?? {};
        current[tableKey].columnsOrder = columnsOrder;
        ReactSession.set(ReactSessionKeys.TABLES_LAYOUT, current);
    }
    SetTableColumnVisibility(tableKey: string, columnsVisibility: GridColumnVisibilityModel) {
        const current = this.tablesLayout;
        current[tableKey] = current[tableKey] ?? {};
        current[tableKey].columnsVisibility = columnsVisibility;
        ReactSession.set(ReactSessionKeys.TABLES_LAYOUT, current);
    }
    SetTablePinnedColumns(tableKey: string, columnsPin: GridPinnedColumnFields) {
        const current = this.tablesLayout;
        current[tableKey] = current[tableKey] ?? {};
        current[tableKey].columnsPin = columnsPin;
        ReactSession.set(ReactSessionKeys.TABLES_LAYOUT, current);
    }
    SetTableColumnFilters(tableKey: string, columnFilters: boolean) {
        const current = this.tablesLayout;
        current[tableKey] = current[tableKey] ?? {};
        current[tableKey].columnFilters = columnFilters;
        ReactSession.set(ReactSessionKeys.TABLES_LAYOUT, current);
    }

    //#endregion

    //#region Standard Getter
    GetMetadata(): DbMetadataFieldInfos[] {
        return this.metadataFields?.map((x) => mapCacheFieldsToBase(x)) ?? [];
    }

    GetOktaCodeVerifier(): string | null {
        if (this.oktaCodeVerifier === null) this.Resync();
        return this.oktaCodeVerifier! ?? null;
    }

    GetOktaPlatformId(): number | null {
        if (this.oktaPid === null) this.Resync();
        return this.oktaPid! ?? null;
    }

    GetCustomerExternalIdLabel(): string {
        return this.currentAuth?.customerExternalIdLabel ?? "";
    }

    GetSpockAgentType(): SpockAgentType | null {
        return this.currentAuth?.spockAgentType ?? null;
    }

    GetAuthKey(): string | undefined {
        return this.currentAuth?.sessionCreated?.key ?? undefined;
    }

    GetUserInfo(): DbUserInfos | undefined {
        return this.currentAuth?.user ?? undefined;
    }

    GetGroup(): DbUserGroupInfos | undefined {
        return this.currentAuth?.group ?? undefined;
    }

    GetACL(): AclTypes[] {
        return this.currentAuth?.group?.acls ?? [];
    }

    GetAppData(): DbApplicationInfos | null {
        return this.appData;
    }

    GetPlatformSettings(): LoginPlatformSettings | null {
        return this.platformSettings;
    }

    GetIsEmulate(): boolean {
        return this.isEmulated;
    }

    IsLoggedIn(): boolean {
        if (this.currentAuth === null) this.Resync();
        return this.currentAuth !== null && moment(this.currentAuth.sessionCreated?.expireDate!) > moment();
    }

    GetLicenses(): Licences[] {
        return this.currentAuth?.licences ?? [];
    }

    UpdateLicenses(lics: Licences[]): void {
        if (!this.currentAuth) return;
        this.currentAuth.licences = lics;
    }

    GetCatchPhrase(): string {
        return this.catchPhrase;
    }

    GetSnackbarEventsEnable() {
        return ReactSession.get(ReactSessionKeys.SNACKBAR_EVENTS_ENABLED) ?? true;
    }

    ToggleSnackbarEventsEnable() {
        ReactSession.set(ReactSessionKeys.SNACKBAR_EVENTS_ENABLED, !this.snackbarEvents);
        this.Resync();
    }

    GetPlatformMode(): PlatformMode {
        return this.currentAuth?.platformMode ?? PlatformMode.Distribution;
    }

    GetAuthLoginResponse(): LoginResponse | null {
        return this.currentAuth;
    }

    GetSharedAccounts(): SharedAccount[] {
        return this.sharedAccounts;
    }

    GetActiveFeatures(): string[] | null {
        let features = this.activeFeatures ?? [];

        // Some features are stored in database user profile
        if (this.GetUserInfo()?.prefs?.audioTracksList && !this.activeFeatures?.includes("Audio Tracks List"))
            features.push("Audio Tracks List");

        return features;
    }

    GetClipListLayout(): "grid" | "list" {
        return this.clipListLayout;
    }

    GetBrowserLayout(): "grid" | "list" {
        return this.browserLayout;
    }

    GetPlatformDefaultPlanningView(): PlanningView | undefined {
        return this.currentAuth?.defaultPlatformPlanningView;
    }

    GetPlatformDefaultPlanningMode(): PlanningMode | undefined {
        return this.currentAuth?.defaultPlatformPlanningMode;
    }

    GetPlatformDefaultPlanningTaskDuration(): string {
        return this.currentAuth?.defaultPlatformPlanningTaskDuration || "00:30";
    }

    GeDragOrderListByKey(key: string): string[] {
        return this.dragOrderLists[key] ?? [];
    }

    GetfilePath(fileId: number): string | undefined {
        return this.downloadedFilesPath[fileId.toString()] || undefined;
    }

    GetDragOrderLists(): any {
        return this.dragOrderLists;
    }

    IsDriveEnabled(): boolean {
        return (
            this.GetPlatformMode() === PlatformMode.Browser ||
            (this.GetPlatformMode() === PlatformMode.Distribution && this.GetLicenses().includes(Licences.Drive))
        );
    }

    GetPlatformDriveHeadId(): number | undefined {
        return this.driveHeadId;
    }

    GetPageFilter(page: string): any {
        return this.pageFilters[page];
    }

    GetPageCache(page: string): any {
        return this.pageCache[page];
    }

    GetDrawerOpen(): boolean {
        return this.drawerOpen;
    }

    GetDensity(page: string): GridDensity | null {
        return this.densityPerPage[page];
    }

    GetLeftPanelWidth(): number | null {
        return this.leftPanelWidth;
    }

    GetRightPanelWidth(): number | null {
        return this.rightPanelWidth;
    }

    GetLastUpdatedMediaTimestamp(): number {
        return this.lastUpdatedMediaTimestamp;
    }
    GetRowsPerPage(page: string): number {
        return this.rowsPerPage[page];
    }

    GetTableUserSetup(tableKey: string) {
        return this.tablesLayout[tableKey] ?? {};
    }

    //#endregion
}
